/**
 * Custom hook to calculate the remaining time until a specified event time.
 * Automatically updates every second and triggers a callback function when the time is up.
 *
 * @param {string} eventTime - The ISO string representing the start time of the event.
 * @param {() => void} onTimeUp - A callback function to execute when the time reaches 0.
 * @returns {{ days: number; hours: number; minutes: number; seconds: number }}
 *          The remaining time in days, hours, minutes, and seconds.
 */
import { useEffect, useState } from 'react';

import calculateTimeLeft from './calculateTimeLeft';

interface TimeLeft {
	days: number;
	hours: number;
	minutes: number;
	seconds: number;
}

const useCountdown = (eventTime: string, onTimeUp: () => void): TimeLeft => {
	const [timeLeft, setTimeLeft] = useState<TimeLeft>(
		() => calculateTimeLeft(eventTime) || { days: 0, hours: 0, minutes: 0, seconds: 0 },
	);

	useEffect(() => {
		const intervalId = setInterval(() => {
			const remainingTime = calculateTimeLeft(eventTime);
			if (!remainingTime) {
				clearInterval(intervalId);
				onTimeUp();
			} else {
				setTimeLeft(remainingTime);
			}
		}, 1000);

		return () => clearInterval(intervalId);
	}, [eventTime, onTimeUp]);

	return timeLeft;
};

export default useCountdown;
